/**
 * Skynet · sk-sticky-buy (módulo 4; ajustes del módulo 18)
 * Barra de compra fija. No tiene lógica de carrito propia: reutiliza el formulario de
 * «SK · Ofertas y compra» (buy() / requestSubmit), así hay un único camino para añadir. Sin ese bloque y
 * con el desplegable activado, la barra lleva el botón sk-atc (también un camino ya existente).
 * Cuándo se enseña (data-trigger):
 *   - button: cuando el botón principal ha quedado entero por encima de la pantalla (por defecto).
 *   - px / percent: tras bajar X píxeles o un % de la página.
 * Se mide en cada scroll (una vez por fotograma) en vez de con IntersectionObserver: un salto brusco (ancla,
 * «subir», tecla Fin) pasa el botón sin que el observador avise, y en ordenador Horizon hace scroll dentro
 * de .page-wrapper, no en la ventana; el scroll se escucha en captura para recoger los dos casos, y la
 * posición se lee con los ayudantes de @theme/scroll-container.
 */
import { getScrollContainer, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const OWN = 'sk-sticky-buy';

class SkStickyBuy extends HTMLElement {
  #mutation = null;
  #ticking = false;
  #ac = null;

  connectedCallback() {
    // Se monta de nuevo en cada conexión (editor, morph de Horizon).
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mutation?.disconnect();
    this.trigger = this.dataset.trigger || 'button';
    this.offers = document.querySelector('sk-offer-cards');
    // Liquid no ve tarjetas anidadas en grupos: si las hay, la barra usa su botón y no el sk-atc propio.
    const atcWrap = this.querySelector('[data-sk-sticky-atc]');
    if (atcWrap && this.offers) {
      atcWrap.remove();
      this.querySelector('[data-sk-sticky-button]')?.removeAttribute('hidden');
    }
    this.button = this.querySelector('[data-sk-sticky-button]:not([hidden])');
    this.buttonPrice = this.button?.querySelector('[data-sk-sticky-button-price]') || null;
    this.ownAtc = this.querySelector('[data-sk-sticky-atc] sk-atc-button');
    this.pick = this.querySelector('[data-sk-sticky-pick]');
    this.price = this.querySelector('[data-sk-sticky-price]');
    this.compare = this.querySelector('[data-sk-sticky-compare]');
    this.save = this.querySelector('[data-sk-sticky-save]');
    this.status = this.querySelector('[data-sk-sticky-status]');
    this.form = this.offers?.querySelector('form') || null;
    // Sin tarjetas: el botón de Horizon. Los «SK · Botón de compra» sueltos (landings) no hacen aparecer la
    // barra, salvo que la barra lleve su propio botón (desplegable activado).
    const candidates = this.offers
      ? [this.offers.querySelector('[data-sk-submit]')]
      : [...document.querySelectorAll('product-form-component [type="submit"], form[action*="/cart/add"] [type="submit"]')];
    this.mainButton = candidates.find((el) => el && !el.closest(OWN) && (this.ownAtc || !el.closest('sk-atc-button'))) || null;
    // Sin forma de comprar desde la barra no hay barra; con el disparo «botón», hace falta el botón principal.
    if (!this.button && !this.ownAtc) return;
    if (this.trigger === 'button' && !this.mainButton) return;
    if (!this.ownAtc && !this.offers && !this.mainButton) return;

    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });
    scrollContainerMediaQuery.addEventListener('change', this.#onScroll, { signal });
    this.#check();
    this.button?.addEventListener('click', this.#onClick, { signal });

    if (this.offers) {
      // Precio y estado del botón: siempre los de la tarjeta elegida.
      this.#mutation = new MutationObserver(() => this.#sync());
      this.#mutation.observe(this.offers, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy', 'checked', 'hidden', 'data-sk-total', 'data-sk-was', 'data-sk-total-text'],
      });
      this.offers.addEventListener('change', this.#sync, { signal });
      if (this.pick) {
        this.#buildPick();
        this.pick.addEventListener('change', this.#onPick, { signal });
      }
    } else if (this.pick) {
      this.pick.remove(); // sin tarjetas no hay nada que elegir aquí
      this.pick = null;
    }
    // Barra con su propio botón (sin tarjetas): el precio de la barra sigue a la variante elegida.
    this.ownAtc?.querySelector('[data-sk-atc-variant]')?.addEventListener('change', this.#onOwnVariant, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#mutation?.disconnect();
    this.#ac?.abort();
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(this.#check);
  };

  #check = () => {
    this.#ticking = false;
    const lost = (this.trigger === 'button' && !this.mainButton?.isConnected) || (this.offers && !this.offers.isConnected);
    const appeared = !this.offers && document.querySelector('sk-offer-cards');
    if (lost || appeared) {
      // Las tarjetas o el botón se han sustituido (morph de Horizon, editor): se vuelve a montar con los nuevos.
      if (this.isConnected) this.connectedCallback();
      return;
    }
    let show = false;
    if (this.trigger === 'px') {
      show = getScrollTop() >= (Number(this.dataset.offsetPx) || 0);
    } else if (this.trigger === 'percent') {
      const box = getScrollContainer();
      const max = box.scrollHeight - box.clientHeight;
      show = max > 0 && (getScrollTop() / max) * 100 >= (Number(this.dataset.offsetPct) || 0);
    } else {
      // Visible solo cuando el botón principal ya está entero por encima del borde superior.
      show = this.mainButton.getBoundingClientRect().bottom < 0;
    }
    if (this.hidden === show) this.hidden = !show;
  };

  #cards() {
    return [...(this.offers?.querySelectorAll('[data-sk-card]') || [])];
  }

  #cardLabel(card) {
    const title = card.querySelector('.sk-offer__title')?.textContent?.trim() || '';
    const price = card.dataset.skTotalText || card.querySelector('[data-sk-price]')?.textContent?.trim() || '';
    return price ? `${title} · ${price}` : title;
  }

  /** Desplegable de la barra: una opción por tarjeta (mismo título y total que la tarjeta). */
  #buildPick() {
    const cards = this.#cards();
    if (cards.length < 2) return;
    this.pick.replaceChildren(
      ...cards.map((card, i) => {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = this.#cardLabel(card);
        return opt;
      })
    );
    this.pick.hidden = false;
  }

  #onPick = () => {
    const card = this.#cards()[Number(this.pick.value)];
    const radio = card?.querySelector('.sk-offer__radio');
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onOwnVariant = (event) => {
    const option = event.currentTarget.selectedOptions?.[0];
    if (!option) return;
    if (this.price && option.dataset.price) this.price.textContent = option.dataset.price;
    if (this.compare) {
      this.compare.textContent = option.dataset.compare || '';
      this.compare.hidden = !option.dataset.compare;
    }
  };

  #sync = () => {
    if (!this.offers) return;
    const cards = this.#cards();
    const card = cards.find((c) => c.querySelector('.sk-offer__radio')?.checked);
    const text = card?.dataset.skTotalText || card?.querySelector('[data-sk-price]')?.textContent?.trim();
    if (text && this.price && this.price.textContent !== text) this.price.textContent = text;
    if (this.buttonPrice) {
      const label = card?.dataset.skTotalText || '';
      if (this.buttonPrice.textContent !== label) this.buttonPrice.textContent = label;
      this.buttonPrice.hidden = !label;
    }
    if (this.pick && !this.pick.hidden) {
      const index = String(cards.indexOf(card));
      if (this.pick.value !== index && index !== '-1') this.pick.value = index;
      cards.forEach((c, i) => {
        const opt = this.pick.options[i];
        const label = this.#cardLabel(c);
        if (opt && opt.textContent !== label) opt.textContent = label;
      });
    }
    // Precio anterior y ahorro: solo si la tarjeta enseña un precio tachado real (el que pone Shopify
    // o un descuento confirmado). skWasText es el importe sin plantilla de texto.
    const shownEl = card?.querySelector('[data-sk-compare]:not([hidden]) [data-sk-compare-value]');
    const compareShown = shownEl ? card.dataset.skWasText || shownEl.textContent.trim() : '';
    const total = Number(card?.dataset.skTotal) || 0;
    const was = Number(card?.dataset.skWas) || 0;
    // Con descuento confirmado (unidades) se usa el mismo porcentaje que enseña la tarjeta; si no,
    // se calcula con enteros y redondeando hacia abajo: nunca más ahorro del que hay.
    const cardPct = Number(card?.dataset.pct) || 0;
    const pct = !compareShown || !(was > total && total > 0) ? 0 : cardPct > 0 ? cardPct : Math.floor(((was - total) * 100) / was);
    if (this.compare) {
      if (this.compare.textContent !== compareShown) this.compare.textContent = compareShown;
      this.compare.hidden = !compareShown;
    }
    if (this.save) {
      const label = pct > 0 ? (this.save.dataset.template || '').replace('__P__', String(pct)) : '';
      if (this.save.textContent !== label) this.save.textContent = label;
      this.save.hidden = !label;
    }
    const busy = this.mainButton?.getAttribute('aria-busy') === 'true';
    // Ocupado: no se puede pulsar. Agotado: se puede, y lleva a elegir otra opción.
    if (this.button) {
      this.button.disabled = busy;
      this.button.setAttribute('aria-busy', String(busy));
    }
    // El aviso del bloque de ofertas queda fuera de la pantalla: se repite aquí (solo visual;
    // los lectores de pantalla ya lo oyen en el original, que es role=status).
    const status = this.offers.querySelector('[data-sk-status]');
    const type = status?.dataset.type || '';
    if (this.status) {
      const msg = type === 'error' || type === 'unavailable' ? status.textContent.trim() : '';
      this.status.textContent = msg;
      this.status.hidden = !msg;
    }
  };

  #onClick = () => {
    const checkout = this.hasAttribute('data-checkout');
    if (this.offers && this.form && !this.mainButton?.disabled) {
      if (typeof this.offers.buy === 'function') {
        this.offers.buy({ checkout });
        return;
      }
      if (typeof this.form.requestSubmit === 'function') {
        // Mismo envío que el botón principal (con su submitter para que el formulario lo trate igual).
        this.form.requestSubmit(this.mainButton);
        return;
      }
    }
    // Sin formulario utilizable (o la opción elegida está agotada): llevar al cliente a elegir.
    const target = this.offers || this.mainButton;
    if (!target) return;
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    (this.offers?.querySelector('.sk-offer__radio:checked, .sk-offer__radio') || this.mainButton)?.focus({ preventScroll: true });
  };
}

if (!customElements.get('sk-sticky-buy')) customElements.define('sk-sticky-buy', SkStickyBuy);
